import React, { useState, useEffect, useRef } from "react";
import Layout from "../components/Layout";
import { API_BASE_URL } from "../constants/api";
import { MdUploadFile, MdCheckCircle, MdError, MdDownload, MdRefresh } from "react-icons/md";

const STATUS_COLORS = {
  QUEUED:     { color: "#888888", bg: "#2a2a2a" },
  PROCESSING: { color: "#ffc20e", bg: "#ffc20e22" },
  COMPLETED:  { color: "#22c55e", bg: "#22c55e22" },
  FAILED:     { color: "#ef4444", bg: "#ef444422" },
};

const FetchBulkDataPage = () => {
  const [file, setFile]         = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [job, setJob]           = useState(null);      // { jobId, totalRows }
  const [status, setStatus]     = useState(null);      // { status, processed, total, failed }
  const [error, setError]       = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [includeAirtelData, setIncludeAirtelData] = useState(true);
  const fileInputRef = useRef(null);
  const pollRef      = useRef(null);

  // ── Polling ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!job) return;
    if (status?.status === "COMPLETED" || status?.status === "FAILED") return;

    pollRef.current = setInterval(async () => {
      try {
        const res  = await fetch(`${API_BASE_URL}/api/sim-report/bulk/${job.jobId}/status`);
        const data = await res.json();
        setStatus(data);
      } catch {
        // network blip — keep polling
      }
    }, 3000);

    return () => clearInterval(pollRef.current);
  }, [job, status?.status]);

  // ── File selection ─────────────────────────────────────────────────────
  const pickFile = (f) => {
    if (!f || !f.name.endsWith(".csv")) {
      setError("Please select a .csv file.");
      return;
    }
    setFile(f);
    setError(null);
  };

  const onFileChange = (e) => pickFile(e.target.files[0]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    pickFile(e.dataTransfer.files[0]);
  };

  // ── Upload ─────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!file) { setError("Please select a CSV file first."); return; }

    setUploading(true);
    setError(null);
    setJob(null);
    setStatus(null);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("includeAirtelData", String(includeAirtelData));

    try {
      const res = await fetch(`${API_BASE_URL}/api/sim-report/bulk`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Upload failed");
      }

      const data = await res.json();
      setJob({ jobId: data.jobId, totalRows: data.totalRows });
      setStatus({ status: "QUEUED", processed: 0, total: data.totalRows, failed: 0 });
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // ── Download ───────────────────────────────────────────────────────────
  const handleDownload = async () => {
    if (!job) return;
    setDownloading(true);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/sim-report/bulk/${job.jobId}/download`);
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `sim_report_${job.jobId.substring(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  // ── Reset ──────────────────────────────────────────────────────────────
  const handleReset = () => {
    clearInterval(pollRef.current);
    setFile(null);
    setJob(null);
    setStatus(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── Derived ────────────────────────────────────────────────────────────
  const pct = status && status.total > 0
    ? Math.min(100, Math.round((status.processed / status.total) * 100))
    : 0;
  const statusTheme = status ? (STATUS_COLORS[status.status] || STATUS_COLORS.QUEUED) : null;
  const isDone = status?.status === "COMPLETED" || status?.status === "FAILED";

  return (
    <Layout title="Fetch Bulk Data">
      <div className="max-w-2xl space-y-6">
        <p style={{ color: "#888888" }}>
          Upload a CSV file containing IMEIs (one per row, first row is header). The system will
          generate a downloadable report with optional Airtel usage and session information.
        </p>

        {/* ── Upload card ── */}
        <div className="rounded-2xl p-8 space-y-6" style={{ backgroundColor: "#1c1c1c", border: "1px solid #2a2a2a" }}>

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            className="rounded-xl flex flex-col items-center justify-center gap-3 py-10 cursor-pointer transition-colors"
            style={{
              border: `2px dashed ${dragging ? "#ffc20e" : "#2a2a2a"}`,
              backgroundColor: dragging ? "#ffc20e11" : "transparent",
            }}
          >
            <MdUploadFile
              className="text-4xl"
              style={{ color: file ? "#ffc20e" : "#555555" }}
            />
            {file ? (
              <div className="text-center">
                <p className="text-sm font-semibold" style={{ color: "#ffffff" }}>{file.name}</p>
                <p className="text-xs mt-1" style={{ color: "#888888" }}>
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-sm" style={{ color: "#aaaaaa" }}>
                  Drag &amp; drop your CSV here, or <span style={{ color: "#ffc20e" }}>browse</span>
                </p>
                <p className="text-xs mt-1" style={{ color: "#555555" }}>Only .csv files accepted</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={onFileChange}
            />
          </div>

          {/* Airtel fetch option */}
          <label
            className="flex items-start gap-3 rounded-xl px-4 py-3"
            style={{ backgroundColor: "#111111", border: "1px solid #2a2a2a", cursor: uploading ? "not-allowed" : "pointer" }}
          >
            <input
              type="checkbox"
              checked={includeAirtelData}
              disabled={uploading || (job && !isDone)}
              onChange={(e) => setIncludeAirtelData(e.target.checked)}
              style={{
                marginTop: "3px",
                width: "16px",
                height: "16px",
                accentColor: "#ffc20e",
                cursor: (uploading || (job && !isDone)) ? "not-allowed" : "pointer",
              }}
            />
            <span className="flex-1">
              <span className="block text-sm font-semibold" style={{ color: "#ffffff" }}>
                Fetch Airtel data usage and last session
              </span>
              <span className="block text-xs mt-1" style={{ color: "#888888" }}>
                Disable to generate the report from local TCU and CAN data only.
              </span>
            </span>
          </label>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 rounded-lg px-4 py-3"
              style={{ backgroundColor: "#ef444422", border: "1px solid #ef444444" }}>
              <MdError style={{ color: "#ef4444", flexShrink: 0 }} />
              <p className="text-sm" style={{ color: "#ef4444" }}>{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleUpload}
              disabled={uploading || !file || (job && !isDone)}
              className="flex-1 py-2.5 text-sm font-semibold rounded-xl transition-colors"
              style={{
                backgroundColor: "#ffc20e",
                color: "#000000",
                opacity: (uploading || !file || (job && !isDone)) ? 0.5 : 1,
                cursor: (uploading || !file || (job && !isDone)) ? "not-allowed" : "pointer",
              }}
            >
              {uploading ? "Submitting…" : "Submit"}
            </button>

            {(job || file) && (
              <button
                onClick={handleReset}
                className="px-4 py-2.5 text-sm font-semibold rounded-xl transition-colors flex items-center gap-2"
                style={{ border: "1px solid #2a2a2a", color: "#aaaaaa" }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#2a2a2a"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
              >
                <MdRefresh /> Reset
              </button>
            )}
          </div>
        </div>

        {/* ── Progress card ── */}
        {job && status && (
          <div className="rounded-2xl p-8 space-y-5" style={{ backgroundColor: "#1c1c1c", border: "1px solid #2a2a2a" }}>

            {/* Header row */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: "#ffffff" }}>Job Progress</p>
                <p className="text-xs mt-0.5 font-mono" style={{ color: "#555555" }}>
                  ID: {job.jobId}
                </p>
              </div>
              <span
                className="px-3 py-1 rounded-full text-xs font-semibold"
                style={{ color: statusTheme.color, backgroundColor: statusTheme.bg }}
              >
                {status.status}
                {status.status === "PROCESSING" && (
                  <span className="ml-1 animate-pulse">●</span>
                )}
              </span>
            </div>

            {/* Progress bar */}
            <div>
              <div className="flex justify-between text-xs mb-2" style={{ color: "#888888" }}>
                <span>{status.processed} of {status.total} processed</span>
                <span>{pct}%</span>
              </div>
              <div className="rounded-full h-2 overflow-hidden" style={{ backgroundColor: "#2a2a2a" }}>
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: status.status === "FAILED" ? "#ef4444" : "#ffc20e",
                  }}
                />
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: "Total",     value: status.total,     color: "#aaaaaa" },
                { label: "Processed", value: status.processed, color: "#22c55e" },
                { label: "Failed",    value: status.failed,    color: status.failed > 0 ? "#ef4444" : "#555555" },
              ].map(({ label, value, color }) => (
                <div key={label} className="rounded-xl px-4 py-3 text-center" style={{ backgroundColor: "#111111" }}>
                  <p className="text-xl font-bold" style={{ color }}>{value}</p>
                  <p className="text-xs mt-0.5" style={{ color: "#555555" }}>{label}</p>
                </div>
              ))}
            </div>

            {/* COMPLETED — download */}
            {status.status === "COMPLETED" && (
              <div className="rounded-xl px-4 py-4 flex items-center justify-between"
                style={{ backgroundColor: "#22c55e11", border: "1px solid #22c55e33" }}>
                <div className="flex items-center gap-2">
                  <MdCheckCircle style={{ color: "#22c55e", fontSize: "1.2rem" }} />
                  <p className="text-sm" style={{ color: "#22c55e" }}>
                    Report ready — {status.total - status.failed} rows succeeded
                    {status.failed > 0 && `, ${status.failed} failed`}
                  </p>
                </div>
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                  style={{
                    backgroundColor: "#22c55e",
                    color: "#000000",
                    opacity: downloading ? 0.6 : 1,
                    cursor: downloading ? "not-allowed" : "pointer",
                  }}
                >
                  <MdDownload />
                  {downloading ? "Downloading…" : "Download CSV"}
                </button>
              </div>
            )}

            {/* FAILED */}
            {status.status === "FAILED" && (
              <div className="flex items-center gap-2 rounded-xl px-4 py-3"
                style={{ backgroundColor: "#ef444411", border: "1px solid #ef444433" }}>
                <MdError style={{ color: "#ef4444" }} />
                <p className="text-sm" style={{ color: "#ef4444" }}>
                  Job failed — the server could not initialise the processing pipeline.
                  Please reset and try again.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FetchBulkDataPage;
